import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export interface PasswordCriteria {
  valid: boolean;
  message: string;
}

export class PasswordUtils {
  private static readonly WeakPasswords = new Set([
    '123456',
    'admin',
    '12345678',
    '123456789',
    '1234',
    '12345',
    'password',
    '123',
    'aa123456',
    '1234567890',
    '1234567',
    '123123',
    '111111',
    '12345678910',
    '000000',
    'admin123',
    '1111',
    'p@ssw0rd',
    'root',
    '654321',
    'qwerty',
    'pass@123',
    '112233',
    '102030',
    'ubnt',
    'abc123',
    'aa@123456',
    'abcd1234',
    '1q2w3e4r',
    '123321',
    'qwertyuiop',
    '87654321',
    '987654321',
    'eliska81',
    '123123123',
    '11223344',
    '0987654321',
    'demo',
    '12341234',
    'qwerty123',
    'admin@123',
    '1q2w3e4r5t',
    '11111111',
    'pass',
    'demo@123',
    'azerty',
    'admintelecom',
    'admin',
    '123meklozed',
    'unknown',
    'shitbird',
    'reset',
  ]);

  static getCriteria(password: any): PasswordCriteria[] {
    return [
      {
        valid: this.isRequiredValid(password),
        message: 'Password is required',
      },
      {
        valid: this.isLengthValid(password),
        message: 'At least 8 characters',
      },
      {
        valid: this.isAsciiValid(password),
        message: 'Allowed: A-Z, a-z, 0-9, !@#$%^&*',
      },
      {
        valid: this.isNotWeakPassword(password),
        message: 'Password is not weak',
      },
    ];
  }

  static isRequiredValid(password: string): boolean {
    return password.length > 0;
  }

  static isLengthValid(password: string): boolean {
    return password.length >= 8;
  }

  static isAsciiValid(password: string): boolean {
    if (!this.isRequiredValid(password)) return false;
    for (let i = 0; i < password.length; i++) {
      const c = password.charCodeAt(i);
      if (c < 33 || c > 126) {
        return false;
      }
    }
    return true;
  }

  static isNotWeakPassword(password: string): boolean {
    if (
      !this.isRequiredValid(password) ||
      !this.isLengthValid(password) ||
      !this.isAsciiValid(password)
    ) {
      return false;
    }

    if (password.length === 0) return false;
    return !this.WeakPasswords.has(password.toLowerCase());
  }

  static passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value || '';

      if (!this.isRequiredValid(value)) {
        return { required: true };
      }

      if (!this.isLengthValid(value)) {
        return { minlength: true };
      }

      if (!this.isAsciiValid(value)) {
        return { ascii: true };
      }

      if (!this.isNotWeakPassword(value)) {
        return { weak: true };
      }

      return null;
    };
  }

  static passwordCompareValidator(passwordKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const password = control.parent.get(passwordKey)?.value || '';
      const confirmPassword = control.value || '';

      if (!confirmPassword) {
        return { required: true };
      }

      if (password !== confirmPassword) {
        return { passwordMismatch: true };
      }

      return null;
    };
  }

  static bindPasswordConfirmation(
    formGroup: FormGroup,
    passwordKey: string,
    confirmPasswordKey: string
  ): void {
    formGroup.get(passwordKey)?.valueChanges.subscribe(() => {
      formGroup
        .get(confirmPasswordKey)
        ?.updateValueAndValidity({ onlySelf: true });
    });
  }
}
